import { Stack } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { BumpNotice } from '#modules/finder/features/posts/components/Discover/BumpNotice';
import { PageHeading } from '#features/ui';

/** Real notice with inert sample data; never reads accounts or changes listings. */
export const Route = createFileRoute('/{-$locale}/_layout/preview/discover-bump')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    previewLocale: search.previewLocale === 'en' ? 'en' : 'ru',
    state: typeof search.state === 'string' ? search.state : 'available',
    long: search.long === true || search.long === 'true',
  }),
  loaderDeps: ({ search }) => ({ locale: search.previewLocale }),
  loader: async ({ deps }) => {
    const { messages } =
      deps.locale === 'ru'
        ? await import('#features/i18n/locales/ru/messages.po')
        : await import('#features/i18n/locales/en/messages.po');
    return { locale: deps.locale, messages };
  },
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: PreviewRoot,
});

function PreviewRoot() {
  const { locale, messages } = Route.useLoaderData();
  const i18n = useMemo(
    () => setupI18n({ locale, messages: { [locale]: messages } }),
    [locale, messages]
  );
  const { state, long } = Route.useSearch();
  return (
    <I18nProvider i18n={i18n}>
      <Preview key={`${state}-${long}`} state={state} long={long} />
    </I18nProvider>
  );
}

function Preview({ state, long }: { state: string; long: boolean }) {
  const [hidden, setHidden] = useState(state === 'cooldown' || state === 'empty');
  const [pending, setPending] = useState(state === 'pending');
  const bump = () => {
    setPending(true);
    setTimeout(() => {
      setHidden(true);
      setPending(false);
    }, 400);
  };
  return (
    <Stack w="full" maxW="1120px" mx="auto" px={{ base: 3, md: 6 }} py={6} gap={4}>
      <PageHeading>Discover</PageHeading>
      {!hidden && (
        <BumpNotice
          gameName={long ? 'Tom Clancy’s Rainbow Six Siege: Championship Edition' : 'Apex Legends'}
          gameImage="/game-bg/cs2.jpg"
          pending={pending}
          onBump={bump}
          onDismiss={() => setHidden(true)}
        />
      )}
    </Stack>
  );
}
